import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Plus, Trash2 } from "lucide-react";

export default function ProductsEditModal({ isOpen, onClose, deal, onSave, isProcessing, moduleSettings }) {
  const [cases, setCases] = useState([]);
  const [services, setServices] = useState([]);
  const [caseTypes, setCaseTypes] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);

  useEffect(() => {
    console.log("=== PRODUCTS MODAL - MODULE SETTINGS ===");
    console.log("Received moduleSettings:", JSON.stringify(moduleSettings, null, 2));
    
    if (moduleSettings) {
      // Extract case types from module settings
      const cases = moduleSettings.cases?.custom_case_types || [];
      console.log("Extracted case types:", JSON.stringify(cases, null, 2));
      setCaseTypes(cases);
      
      // Extract service types from module settings
      const services = moduleSettings.services?.custom_service_types || [];
      console.log("Extracted service types:", JSON.stringify(services, null, 2));
      setServiceTypes(services);
    }
  }, [moduleSettings]);

  useEffect(() => {
    if (deal) {
      // Load existing cases and services
      setCases(deal.cases || []);
      setServices(deal.services || []);
    }
  }, [deal]);

  const addCase = () => {
    setCases([...cases, {
      case_type: "",
      case_pricing_type: "flat",
      case_price: 0,
      case_quantity: 1
    }]);
  };

  const removeCase = (index) => {
    setCases(cases.filter((_, i) => i !== index));
  };

  const updateCase = (index, field, value) => {
    const updated = [...cases];
    updated[index] = { ...updated[index], [field]: value };
    setCases(updated);
  };

  const handleCaseTypeChange = (index, value) => {
    const selectedType = caseTypes.find(t => t.id === value);
    if (selectedType) {
      const updated = [...cases];
      updated[index] = {
        ...updated[index],
        case_type: value,
        case_pricing_type: selectedType.pricing_type || "flat",
        case_price: selectedType.default_price || 0
      };
      setCases(updated);
    } else {
      updateCase(index, 'case_type', value);
    }
  };

  const addService = () => {
    setServices([...services, {
      service_type: "",
      service_pricing_type: "flat",
      service_price: 0,
      service_quantity: 1
    }]);
  };

  const removeService = (index) => {
    setServices(services.filter((_, i) => i !== index));
  };

  const updateService = (index, field, value) => {
    const updated = [...services];
    updated[index] = { ...updated[index], [field]: value };
    setServices(updated);
  };

  const handleServiceTypeChange = (index, value) => {
    const selectedType = serviceTypes.find(t => t.id === value);
    if (selectedType) {
      const updated = [...services];
      updated[index] = {
        ...updated[index],
        service_type: value,
        service_pricing_type: selectedType.pricing_type || "flat",
        service_price: selectedType.default_price || 0
      };
      setServices(updated);
    } else {
      updateService(index, 'service_type', value);
    }
  };

  const calculateTotal = () => {
    let total = 0;
    
    cases.forEach(caseItem => {
      const caseTotal = caseItem.case_pricing_type === "variable"
        ? (caseItem.case_price || 0) * (caseItem.case_quantity || 1)
        : (caseItem.case_price || 0);
      total += caseTotal;
    });
    
    services.forEach(serviceItem => {
      const serviceTotal = serviceItem.service_pricing_type === "variable"
        ? (serviceItem.service_price || 0) * (serviceItem.service_quantity || 1)
        : (serviceItem.service_price || 0);
      total += serviceTotal;
    });
    
    return total;
  };

  const handleSave = () => {
    let deal_type = "case";
    if (cases.length > 0 && services.length > 0) deal_type = "both";
    else if (services.length > 0) deal_type = "service";
    
    const updatedDeal = {
      deal_type,
      cases: cases.map(c => ({
        ...c,
        case_price: Number(c.case_price),
        case_quantity: Number(c.case_quantity)
      })),
      services: services.map(s => ({
        ...s,
        service_price: Number(s.service_price),
        service_quantity: Number(s.service_quantity)
      })),
      total_value: calculateTotal()
    };
    
    onSave(updatedDeal);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar Produtos e Serviços</DialogTitle>
          <DialogDescription>
            Adicione ou modifique os processos e serviços incluídos neste negócio
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Cases Section */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">Processos</h3>
              <Button
                type="button"
                onClick={addCase}
                size="sm"
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Adicionar Processo
              </Button>
            </div>

            {cases.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">Nenhum processo adicionado</p>
            ) : (
              <div className="space-y-4">
                {cases.map((caseItem, index) => (
                  <Card key={index} className="border-blue-200 bg-blue-50/30">
                    <CardContent className="p-4 space-y-4">
                      <div className="flex justify-between items-start">
                        <h4 className="font-semibold text-blue-900">Processo #{index + 1}</h4>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeCase(index)}
                          className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label>Tipo de Processo</Label>
                          {caseTypes.length === 0 ? (
                            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                              <p className="text-sm text-amber-800">
                                Nenhum tipo de processo configurado. Acesse Configurações → Módulos → Processos para cadastrar.
                              </p>
                            </div>
                          ) : (
                            <Select
                              value={caseItem.case_type}
                              onValueChange={(value) => handleCaseTypeChange(index, value)}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Selecione o tipo de processo" />
                              </SelectTrigger>
                              <SelectContent>
                                {caseTypes.map(type => (
                                  <SelectItem key={type.id} value={type.id}>
                                    {type.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>

                        <div>
                          <Label>Tipo de Cobrança</Label>
                          <Select
                            value={caseItem.case_pricing_type}
                            onValueChange={(value) => updateCase(index, 'case_pricing_type', value)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="flat">Valor Fixo</SelectItem>
                              <SelectItem value="variable">Variável (por Quantidade)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <Label>Preço {caseItem.case_pricing_type === "variable" && "(por unidade)"}</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={caseItem.case_price}
                            onChange={(e) => updateCase(index, 'case_price', e.target.value)}
                            placeholder="0,00"
                          />
                        </div>

                        {caseItem.case_pricing_type === "variable" && (
                          <div>
                            <Label>Quantidade</Label>
                            <Input
                              type="number"
                              value={caseItem.case_quantity}
                              onChange={(e) => updateCase(index, 'case_quantity', e.target.value)}
                              min="1"
                            />
                          </div>
                        )}
                      </div>

                      <div className="pt-3 border-t border-blue-200">
                        <p className="text-sm text-slate-600">Total do Processo:</p>
                        <p className="text-xl font-bold text-blue-700">
                          ${(caseItem.case_pricing_type === "variable"
                            ? (caseItem.case_price || 0) * (caseItem.case_quantity || 1)
                            : (caseItem.case_price || 0)
                          ).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Services Section */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">Serviços</h3>
              <Button
                type="button"
                onClick={addService}
                size="sm"
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Adicionar Serviço
              </Button>
            </div>

            {services.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">Nenhum serviço adicionado</p>
            ) : (
              <div className="space-y-4">
                {services.map((serviceItem, index) => (
                  <Card key={index} className="border-purple-200 bg-purple-50/30">
                    <CardContent className="p-4 space-y-4">
                      <div className="flex justify-between items-start">
                        <h4 className="font-semibold text-purple-900">Serviço #{index + 1}</h4>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeService(index)}
                          className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label>Tipo de Serviço</Label>
                          {serviceTypes.length === 0 ? (
                            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                              <p className="text-sm text-amber-800">
                                Nenhum tipo de serviço configurado. Acesse Configurações → Módulos → Serviços para cadastrar.
                              </p>
                            </div>
                          ) : (
                            <Select
                              value={serviceItem.service_type}
                              onValueChange={(value) => handleServiceTypeChange(index, value)}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Selecione o tipo de serviço" />
                              </SelectTrigger>
                              <SelectContent>
                                {serviceTypes.map(type => (
                                  <SelectItem key={type.id} value={type.id}>
                                    {type.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>

                        <div>
                          <Label>Tipo de Cobrança</Label>
                          <Select
                            value={serviceItem.service_pricing_type}
                            onValueChange={(value) => updateService(index, 'service_pricing_type', value)}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="flat">Valor Fixo</SelectItem>
                              <SelectItem value="variable">Variável (por Quantidade)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <Label>Preço {serviceItem.service_pricing_type === "variable" && "(por unidade)"}</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={serviceItem.service_price}
                            onChange={(e) => updateService(index, 'service_price', e.target.value)}
                            placeholder="0,00"
                          />
                        </div>

                        {serviceItem.service_pricing_type === "variable" && (
                          <div>
                            <Label>Quantidade</Label>
                            <Input
                              type="number"
                              value={serviceItem.service_quantity}
                              onChange={(e) => updateService(index, 'service_quantity', e.target.value)}
                              min="1"
                            />
                          </div>
                        )}
                      </div>

                      <div className="pt-3 border-t border-purple-200">
                        <p className="text-sm text-slate-600">Total do Serviço:</p>
                        <p className="text-xl font-bold text-purple-700">
                          ${(serviceItem.service_pricing_type === "variable"
                            ? (serviceItem.service_price || 0) * (serviceItem.service_quantity || 1)
                            : (serviceItem.service_price || 0)
                          ).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Total */}
          {(cases.length > 0 || services.length > 0) && (
            <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
              <p className="text-sm text-slate-600 mb-1">Novo Total do Negócio:</p>
              <p className="text-3xl font-bold text-blue-600">
                ${calculateTotal().toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isProcessing}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              disabled={isProcessing || (cases.length === 0 && services.length === 0)}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isProcessing ? "Salvando..." : "Salvar Alterações"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}